import React, { useState } from "react";
import {
  updatePassword,
  signIn,
  resetPassword,
  ResetPasswordOutput,
  confirmResetPassword,
  confirmSignIn,
} from "aws-amplify/auth";
import { useNavigate, useLocation } from "react-router-dom";
import { get, post } from "aws-amplify/api";
import Avatar from "@material-ui/core/Avatar";
import Box from "@material-ui/core/Box";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";

import { Copyright } from "../../common/Copyright";
import { LoginForm } from "./LoginForm";
import SignupForm from "./SignUpForm";
import { ChangePasswordForm } from "./ChangePassword";
import { useSessionContext } from "../../contexts/SessionContext";

// Added Dialog imports for the account status message
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";

const {
  CognitoIdentityProviderClient,
  InitiateAuthCommand,
  SignUpCommand,
  AdminAddUserToGroupCommand,
} = require("@aws-sdk/client-cognito-identity-provider");
const AmazonCognitoIdentity = require("amazon-cognito-identity-js");

const useStyles = makeStyles((theme) => ({
  box: {
    marginTop: theme.spacing(8),
  },
  paper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  avatar: {
    margin: theme.spacing(1),
  },
  toggleLink: {
    color: theme.palette.primary.main,
    cursor: "pointer",
    textDecoration: "underline",
    marginTop: theme.spacing(2),
  },
}));

export const Login = (props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [changePassword, setChangePassword] = useState(false);
  const [username, setUsername] = useState("");
  const [signInResponse, setSignInResponse] = useState(undefined);
  // state for controlling the account status dialog
  const [openAuthDialog, setOpenAuthDialog] = useState(false);
  const [authDialogMessage, setAuthDialogMessage] = useState("");

  const classes = useStyles();
  const navigate = useNavigate();
  const { state } = useLocation();

  const { userHasAuthenticated } = useSessionContext();

  // AWS Cognito configurations
  const poolData = {
    UserPoolId: process.env.REACT_APP_USER_POOL_ID,
    ClientId: process.env.REACT_APP_APP_CLIENT_ID,
  };

  const cognitoClient = new CognitoIdentityProviderClient({
    region: process.env.REACT_APP_REGION,
  });

  const handleForgotPassword = async (username, onEmailed) => {
    try {
      const output = await resetPassword({ username });
      const { nextStep } = output;

      switch (nextStep.resetPasswordStep) {
        case "CONFIRM_RESET_PASSWORD_WITH_CODE":
          const codeDeliveryDetails = nextStep.codeDeliveryDetails;
          console.log(
            `Confirmation code was sent to ${codeDeliveryDetails.deliveryMedium}`
          );
          console.log(output);
          onEmailed(true);
          break;
        case "DONE":
          console.log("Successfully reset password.");
          break;
      }
    } catch (e) {
      alert(e.message);
      console.log(e);
    }
  };

  const handleConfirmResetPassword = async (
    username,
    newPassword,
    confirmationCode
  ) => {
    try {
      const res = await confirmResetPassword({
        username,
        newPassword,
        confirmationCode,
      });
      console.log(res);
    } catch (e) {
      alert(e.message);
      console.log(e);
    }
  };
  const handleSubmit = async (username, password) => {
    setIsLoading(true);
    try {
      setUsername(username);
      const signInResponse = await signIn({ username, password });

      const authParams = {
        AuthFlow: "USER_PASSWORD_AUTH",
        ClientId: poolData.ClientId,
        AuthParameters: {
          USERNAME: username,
          PASSWORD: password,
        },
      };

      const command = new InitiateAuthCommand(authParams);
      const result = await cognitoClient.send(command);

      const accessToken = result.AuthenticationResult.AccessToken;
      const idToken = result.AuthenticationResult.IdToken;
      const refreshToken = result.AuthenticationResult.RefreshToken;
      const expiryTime = result.AuthenticationResult.ExpiresIn

      sessionStorage.setItem("idToken", idToken);
      sessionStorage.setItem("refreshToken",refreshToken)
      sessionStorage.setItem("expiryTime", expiryTime)
      if (
        signInResponse.nextStep.signInStep ===
        "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
      ) {
        setIsLoading(false);
        setChangePassword(true);
        setSignInResponse(signInResponse);
      } else {
        userHasAuthenticated(username);
        navigate(state?.from || "/");
      }
    } catch (e) {
      alert(e.message);
      setIsLoading(false);
    }
  };

  const handleSignUp = async (username, password, email) => {
    if (!username || !password || !email) {
      alert("Username, password, and email are required.");
      return;
    }

    setIsLoading(true);
    const signUpParams = {
      ClientId: poolData.ClientId,
      Username: username,
      Password: password,
      UserAttributes: [{ Name: "email", Value: email }],
    };

    try {
      // Sign up the user
      const command = new SignUpCommand(signUpParams);
      const result = await cognitoClient.send(command);

      if (!result.UserConfirmed) {
        setAuthDialogMessage(
          "Sign up successful! Your account is pending admin confirmation."
        );
        setOpenAuthDialog(true);
        setIsSignUp(false);
        return;
      }

      //Payload for the backend API /User/addgroup
      const addToGroupParams = {
        UserPoolId: poolData.UserPoolId,
        Username: username,
        GroupName: "Editor",
      };
      // Coomented for future use - Add group to user
      // await post({
      //   apiName: "api",
      //   path: `User/addgroup`,
      //   options: { body: addToGroupParams },
      // });
      // const responseData = await response.json();
      // if (!response.ok)
      //   throw new Error(responseData.error || "Failed to add user to group");

      setAuthDialogMessage(
        "Sign up successful! User has been added to the group."
      );
      setOpenAuthDialog(true);
      setIsSignUp(false);
    } catch (err) {
      console.error("Sign up failed:", err);
      alert("Sign up failed: " + (err.message || "Unknown error"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Grid
        container
        spacing={0}
        direction="column"
        alignItems="center"
        style={{
          minHeight: "75vh",
          justifyContent: "center",
        }}
      >
        {changePassword ? (
          <Grid className={classes.paper} item>
            <Avatar className={classes.avatar}>
              <LockOutlinedIcon color="primary" />
            </Avatar>
            <Typography component="h1" variant="h5">
              Update your password
            </Typography>
            <Typography component="h1" variant="caption">
              You need to update your password because this is the first time
              you are signing in.
            </Typography>
            <ChangePasswordForm
              isLoading={isLoading}
              onSubmit={confirmSignIn}
              username={username}
            />
            <Box mt={8} className={classes.box}>
              <Copyright />
            </Box>
          </Grid>
        ) : isSignUp ? (
          <Grid className={classes.paper} item>
            <Avatar className={classes.avatar}>
              <LockOutlinedIcon color="primary" />
            </Avatar>
            <Typography component="h1" variant="h5">
              Sign Up
            </Typography>
            <SignupForm
              isLoading={isLoading}
              onSubmit={handleSignUp}
              onSwitchToSignIn={() => setIsSignUp(false)}
              // onClick={() => setIsSignUp(false)}
            />
            <Box mt={2}>
              <Typography
                variant="body2"
                align="center"
                className={classes.toggleLink}
                onClick={() => setIsSignUp(false)}
              >
                Already have an account? Sign In
              </Typography>
            </Box>
            <Box mt={8} className={classes.box}>
              <Copyright />
            </Box>
          </Grid>
        ) : (
          <Grid className={classes.paper} item>
            <Avatar className={classes.avatar}>
              <LockOutlinedIcon color="primary" />
            </Avatar>
            <LoginForm
              isLoading={isLoading}
              onSubmit={handleSubmit}
              onForgotPassword={handleForgotPassword}
              onPasswordChangeSubmit={handleConfirmResetPassword}
            />
            <Box mt={2}>
              <Typography
                variant="body2"
                align="center"
                className={classes.toggleLink}
                onClick={() => setIsSignUp(true)}
              >
                Don't have an account? Sign Up
              </Typography>
            </Box>

            <Box mt={8} className={classes.box}>
              <Copyright />
            </Box>
          </Grid>
        )}
      </Grid>

      {/* Dialog to inform the user about account confirmation/authentication */}
      <Dialog open={openAuthDialog} onClose={() => setOpenAuthDialog(false)}>
        <DialogTitle>Account Status</DialogTitle>
        <DialogContent>
          <Typography>{authDialogMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAuthDialog(false)} color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
